// src/screens/FreeTranslation.jsx
// Freie Übersetzung: ganze Sätze selbst eintippen. Anfangs meist Spanisch → Deutsch,
// mit dem Kursfortschritt immer mehr Deutsch → Spanisch (translationPlan.js).
// Sätze und Wochenfilter teilt sich die Übung mit den Lückentexten (sentencePool.js).
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../App.css";
import { loadSentences } from "../data/loadSentences";
import { loadVerbFormsForCloze } from "../data/loadConjugations";
import { loadFunctionWords } from "../data/loadConcepts";
import { loadWeeklyWordsUpToWeek } from "../data/loadWeeklyWords";
import { languageLabel, parseLanguageId } from "../data/languages";
import { TARGET_WORDS } from "../data/learningConfig";
import {
  TO_LEARNING,
  getDirectionForTask,
  getToLearningShare,
  getTranslationWeekTarget,
  getWeeksTotal,
} from "../data/translationPlan";
import {
  addAcceptedVariant,
  loadAcceptedVariants,
  loadRecentSentenceIds,
  loadSolvedSentenceIds,
  loadTranslationWeekCount,
  markSentenceSolved,
  pushRecentSentenceId,
  saveTranslationWeekCount,
  syncTranslationSentences,
} from "../data/translationProgressStorage";
import { buildSpanishVerbFormsSet } from "../utils/sentenceClozeHelpers";
import { pickSentenceForCloze } from "../utils/clozeSelectionHelpers";
import {
  buildKnownWordsSet,
  filterSentencesForWeek,
  getKnownTopicWords,
  getTargetDifficulty,
} from "../utils/sentencePool";
import { loadTopics } from "../data/loadTopics";
import { checkTranslation, diffWords, isSolved } from "../utils/translationCheck";
import SpeakButton from "../components/SpeakButton";
import {
  Badge,
  Button,
  Note,
  Panel,
  ProgressBar,
  ScreenHeader,
  Stack,
} from "../components/ui";

// Sonderzeichen-Leiste über dem Eingabefeld, je nach Sprache der Antwort
const SPECIAL_CHARS = {
  es: ["á", "é", "í", "ó", "ú", "ñ", "ü", "¿", "¡"],
  de: ["ä", "ö", "ü", "ß"],
  fr: ["é", "è", "ê", "à", "â", "ç", "ù", "û", "ô", "î", "ï", "ë", "œ"],
};

// Rückmeldung nach dem Prüfen
const FEEDBACK = {
  correct: { tone: "success", text: "Richtig!" },
  accent: { tone: "success", text: "Richtig! Achte noch auf die Akzente." },
  elision: { tone: "success", text: "Richtig! Achte noch auf die Verkürzung vor Vokal (j'ai, l'eau)." },
  ne: { tone: "success", text: "Richtig! Geschrieben gehört zur Verneinung noch „ne“ (je ne sais pas)." },
  typo: { tone: "success", text: "Fast perfekt, nur ein kleiner Tippfehler." },
  order: {
    tone: "info",
    text: "Die Wörter stimmen, die Reihenfolge ist aber anders. Passt deine Version auch?",
  },
  wrong: { tone: "error", text: "Nicht ganz. Vergleiche mit der Musterlösung." },
  shown: { tone: "info", text: "So lautet die Musterlösung." },
  accepted: { tone: "success", text: "Alles klar, zählt als richtig." },
};

function DiffLine({ words }) {
  return (
    <p className="translation-diff">
      {words.map((word, idx) => (
        <span
          key={idx}
          className={`translation-diff__word translation-diff__word--${word.status}`}
        >
          {word.text}
        </span>
      ))}
    </p>
  );
}

export default function FreeTranslation({
  language,
  pace,
  profileId,
  weekIndex,
  onBack,
}) {
  const langId = language || "es-de";
  const paceValue = pace || "medium";
  const profile = profileId || "default";
  const week = weekIndex || 1;

  const pair = parseLanguageId(langId) || { source: "es", target: "de" };
  const learningLang = pair.source;
  const nativeLang = pair.target;

  const weeklyWords = Math.ceil(TARGET_WORDS / getWeeksTotal(paceValue));
  const weekTarget = getTranslationWeekTarget(week, paceValue);
  const toLearningShare = getToLearningShare(week, paceValue);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [allSentences, setAllSentences] = useState([]);
  const [verbs, setVerbs] = useState([]);
  const [functionWords, setFunctionWords] = useState(new Set());
  const [weeklyWordList, setWeeklyWordList] = useState([]);
  // Themen-Einträge (Zahlen, Uhrzeit …), ihre Wörter gelten nach der Themen-Woche als bekannt
  const [topics, setTopics] = useState([]);

  // Wochenzähler (null = noch nicht geladen) und laufende Nummer der Aufgabe in dieser Woche
  const [weekCount, setWeekCount] = useState(null);
  const [taskIndex, setTaskIndex] = useState(null);

  const [task, setTask] = useState(null); // { sentence, direction }
  const [answer, setAnswer] = useState("");
  // null = noch nicht geprüft, sonst Schlüssel aus FEEDBACK
  const [result, setResult] = useState(null);

  const inputRef = useRef(null);

  const verbFormsSet = useMemo(() => buildSpanishVerbFormsSet(verbs), [verbs]);
  const topicWords = useMemo(
    () => getKnownTopicWords(topics, week, paceValue, learningLang),
    [topics, week, paceValue, learningLang]
  );
  const knownWordsSet = useMemo(
    () => buildKnownWordsSet(weeklyWordList, verbs, functionWords, langId, topicWords),
    [weeklyWordList, verbs, functionWords, langId, topicWords]
  );

  // Sätze, Verbformen, Funktionswörter und bisherige Wochenwörter laden, Fortschritt abgleichen
  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoading(true);
      setError(null);
      try {
        const [sentences, verbList, funcWords, words, , topicEntries] = await Promise.all([
          loadSentences(learningLang),
          loadVerbFormsForCloze(learningLang),
          loadFunctionWords(learningLang),
          loadWeeklyWordsUpToWeek({
            langId,
            pace: paceValue,
            weeklyWords,
            profile,
            weekIndex: week,
          }),
          // gelöste Sätze/Varianten von anderen Geräten, bevor der erste Satz gewählt wird
          syncTranslationSentences(profile, langId),
          // fehlen die Themen der Lernsprache, geht es ohne weiter
          loadTopics(learningLang).catch(() => []),
        ]);
        if (cancelled) return;
        setAllSentences(sentences);
        setVerbs(verbList);
        setFunctionWords(funcWords);
        setWeeklyWordList(words);
        setTopics(topicEntries);
        if (sentences.length === 0) {
          setError(`Für ${languageLabel(learningLang)} gibt es noch keine Sätze.`);
        }
      } catch (e) {
        console.error("FreeTranslation: Laden fehlgeschlagen:", e);
        if (!cancelled) setError(e.message || "Unbekannter Fehler beim Laden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [langId, learningLang, paceValue, weeklyWords, profile, week]);

  // Wochenzähler laden
  useEffect(() => {
    let cancelled = false;
    loadTranslationWeekCount(profile, langId, paceValue, week).then((count) => {
      if (cancelled) return;
      setWeekCount(count);
      setTaskIndex(count);
    });
    return () => {
      cancelled = true;
    };
  }, [profile, langId, paceValue, week]);

  function pickTask(index) {
    const direction = getDirectionForTask(index, toLearningShare);
    const pool = filterSentencesForWeek(
      allSentences,
      week,
      paceValue,
      verbFormsSet,
      loadRecentSentenceIds(profile, langId),
      pair.source
    );
    // Noch nicht gelöste Sätze dieser Richtung zuerst
    const solved = loadSolvedSentenceIds(profile, langId, direction);
    const unsolved = pool.filter((s) => !solved.has(s.id));

    const sentence = pickSentenceForCloze(
      unsolved.length > 0 ? unsolved : pool,
      knownWordsSet,
      {
        minKnownRatio: 0.9,
        relaxKnownRatio: 0.8,
        hardMinKnownRatio: 0.7,
        // In die Lernsprache höchstens mittelschwere Sätze
        maxDifficulty: direction === TO_LEARNING ? 0.5 : 1.0,
        targetDifficulty: getTargetDifficulty(week, paceValue),
      }
    );
    if (!sentence) return null;

    pushRecentSentenceId(profile, langId, sentence.id);
    return { sentence, direction };
  }

  // Ersten Satz wählen, sobald alles geladen ist
  useEffect(() => {
    if (loading || error || task || taskIndex === null) return;
    if (allSentences.length === 0) return;
    const next = pickTask(taskIndex);
    if (next) setTask(next);
    else setError("Keine passenden Sätze für dein aktuelles Niveau gefunden.");
    // pickTask liest nur Werte, die hier schon in den Abhängigkeiten stehen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, task, taskIndex, allSentences, knownWordsSet]);

  // Je nach Richtung: Vorgabe, Musterlösung und Sprache der Antwort
  const toLearning = task?.direction === TO_LEARNING;
  const prompt = task ? (toLearning ? task.sentence.src : task.sentence.target) : "";
  const solution = task ? (toLearning ? task.sentence.target : task.sentence.src) : "";
  const promptLang = toLearning ? nativeLang : learningLang;
  const answerLang = toLearning ? learningLang : nativeLang;

  async function countAsSolved() {
    markSentenceSolved(profile, langId, task.direction, task.sentence.id);
    const newCount = (weekCount || 0) + 1;
    setWeekCount(newCount);
    await saveTranslationWeekCount(profile, langId, paceValue, week, newCount);
  }

  function handleCheck() {
    if (!task || result) return;
    const status = checkTranslation(answer, solution, {
      lang: answerLang,
      acceptedVariants: loadAcceptedVariants(profile, langId, task.direction, task.sentence.id),
    });
    if (status === "empty") {
      inputRef.current?.focus();
      return;
    }
    setResult(status);
    if (isSolved(status)) countAsSolved();
  }

  function handleShowSolution() {
    if (!task || result) return;
    setResult("shown");
  }

  function handleAccept() {
    if (!task || (result !== "wrong" && result !== "order")) return;
    addAcceptedVariant(profile, langId, task.direction, task.sentence.id, answer.trim());
    setResult("accepted");
    countAsSolved();
  }

  function handleNext() {
    const nextIndex = (taskIndex || 0) + 1;
    const next = pickTask(nextIndex);
    setTaskIndex(nextIndex);
    setAnswer("");
    setResult(null);
    if (next) {
      setTask(next);
      // Fokus zurück ins Feld, damit man direkt weitertippen kann
      setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      setError("Keine weiteren passenden Sätze gefunden.");
    }
  }

  function handleKeyDown(e) {
    if (e.key !== "Enter" || e.shiftKey) return;
    e.preventDefault();
    if (result) handleNext();
    else handleCheck();
  }

  // Sonderzeichen an der Cursor-Position einfügen
  function insertChar(char) {
    const el = inputRef.current;
    if (!el || result) return;
    const start = el.selectionStart ?? answer.length;
    const end = el.selectionEnd ?? answer.length;
    const next = answer.slice(0, start) + char + answer.slice(end);
    setAnswer(next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + char.length, start + char.length);
    }, 0);
  }

  const title = `Freie Übersetzung – Woche ${week}`;
  const backButton = (
    <Button block variant="secondary" onClick={onBack}>
      Zurück zum Hauptmenü
    </Button>
  );

  if (error) {
    return (
      <>
        <ScreenHeader title={title} />
        <Note tone="error">Fehler: {error}</Note>
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  if (loading || !task) {
    return (
      <>
        <ScreenHeader title={title} />
        <Note>fluent! sucht passende Sätze für dich …</Note>
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  const done = Math.min(weekCount || 0, weekTarget);
  const feedback = result ? FEEDBACK[result] : null;
  const showAnswerDiff = result && result !== "shown" && answer.trim() !== "";
  const diff = result ? diffWords(answer, solution) : null;
  const canAccept = result === "wrong" || result === "order";
  const inputStatus =
    result === "correct" || result === "accepted"
      ? "correct"
      : ["accent", "elision", "ne", "typo", "order"].includes(result)
      ? "almost"
      : result === "wrong"
      ? "wrong"
      : undefined;

  return (
    <>
      <ScreenHeader
        title={title}
        subtitle={`Übersetze ins ${languageLabel(answerLang)}e`}
      />

      <ProgressBar
        className="translation-progress"
        value={done / weekTarget}
        tone={done >= weekTarget ? "right" : "blue"}
        label="Diese Woche"
        valueLabel={`${done} / ${weekTarget} Sätze`}
      />

      <Panel variant="muted" className="exercise-prompt">
        <Badge tone="blue">
          {languageLabel(promptLang)} → {languageLabel(answerLang)}
        </Badge>
        <div className="exercise-prompt__text">{prompt}</div>
        {promptLang === learningLang && <SpeakButton text={prompt} lang={promptLang} />}
      </Panel>

      <Stack gap={3}>
        <div>
          <textarea
            ref={inputRef}
            className={`ui-input translation-input${
              inputStatus ? ` ui-input--${inputStatus}` : ""
            }`}
            rows={2}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={handleKeyDown}
            readOnly={!!result}
            placeholder="Deine Übersetzung …"
            aria-label="Deine Übersetzung"
            autoCapitalize="sentences"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
          />
          {!result && SPECIAL_CHARS[answerLang] && (
            <div className="translation-chars" aria-label="Sonderzeichen">
              {SPECIAL_CHARS[answerLang].map((char) => (
                <button
                  key={char}
                  type="button"
                  className="translation-chars__key"
                  // mousedown statt click: das Feld behält den Fokus, die Tastatur bleibt offen
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertChar(char);
                  }}
                >
                  {char}
                </button>
              ))}
            </div>
          )}
        </div>

        {feedback && (
          <Note tone={feedback.tone} className="exercise-feedback">
            {feedback.text}
          </Note>
        )}

        {result && (
          <Panel className="translation-result">
            {showAnswerDiff && (
              <>
                <span className="ui-label">Deine Antwort</span>
                <DiffLine words={diff.answer} />
              </>
            )}
            <span className="ui-label">Musterlösung</span>
            <div className="translation-result__solution">
              <DiffLine words={showAnswerDiff ? diff.solution : diffWords(solution, solution).solution} />
              {answerLang === learningLang && <SpeakButton text={solution} lang={answerLang} />}
            </div>
          </Panel>
        )}

        {result ? (
          <>
            {canAccept && (
              <Button block variant="secondary" onClick={handleAccept}>
                War auch richtig
              </Button>
            )}
            <Button block onClick={handleNext}>
              Nächster Satz
            </Button>
          </>
        ) : (
          <>
            <Button block onClick={handleCheck} disabled={!answer.trim()}>
              Prüfen
            </Button>
            <Button block variant="ghost" onClick={handleShowSolution}>
              Lösung zeigen
            </Button>
          </>
        )}
      </Stack>

      <div className="exercise-actions-tight">{backButton}</div>
    </>
  );
}
